/**
 * G8 voice mode state machine (pure): push-to-talk, optional hands-free listening, barge-in and
 * cancel. The reducer returns effects the host performs (recognizer, speech synthesis, the turn
 * handler), so the same machine drives the PWA today and a native iOS/watchOS host later.
 *
 *   idle ──PTT_DOWN──▶ listening ──(PTT_UP, RECOGNITION_END)──▶ thinking ──SPEAK──▶ speaking
 *     ▲                    ▲                                        │                  │
 *     └──── CANCEL ────────┴──────── TURN_DONE / SPEECH_DONE ───────┴──────────────────┘
 *
 * Hands-free keeps the recognizer running (restarting it after silence) and treats each final
 * result as one utterance. While speaking, what the recognizer hears is compared against what is
 * being said: the device's own voice (echo) is ignored, the user talking over it interrupts.
 */

export type VoiceStatus = 'idle' | 'listening' | 'thinking' | 'speaking'

export interface VoiceState {
  status: VoiceStatus
  handsFree: boolean
  pttHeld: boolean
  /** Live (not yet final) recognition text. */
  interim: string
  /** Finals collected while push-to-talk is held. */
  buffer: string
  /** What is being (or was last) spoken: captions and the echo guard. */
  caption: string
  /** Last utterance handed to the turn handler. */
  heard?: string
  error?: string
}

export type VoiceEvent =
  | { type: 'PTT_DOWN' }
  | { type: 'PTT_UP' }
  | { type: 'TOGGLE_HANDS_FREE' }
  | { type: 'INTERIM'; text: string }
  | { type: 'FINAL'; text: string }
  | { type: 'RECOGNITION_END' }
  | { type: 'RECOGNITION_ERROR'; error: string }
  /** Speak a reply (or the next streamed chunk of one). */
  | { type: 'SPEAK'; text: string }
  /** The speech queue drained and no more chunks are coming. */
  | { type: 'SPEECH_DONE' }
  /** The turn finished without anything to say. */
  | { type: 'TURN_DONE' }
  | { type: 'CANCEL' }
  /** Typed input (no speech recognition, or the user prefers to type): one utterance. */
  | { type: 'TEXT'; text: string }

export type VoiceEffect =
  | { type: 'listen'; continuous: true }
  | { type: 'stopListening' }
  | { type: 'abortListening' }
  | { type: 'speak'; text: string }
  | { type: 'cancelSpeech' }
  | { type: 'handle'; text: string }
  | { type: 'cancelTurn' }

export const initialVoice = (): VoiceState => ({
  status: 'idle',
  handsFree: false,
  pttHeld: false,
  interim: '',
  buffer: '',
  caption: '',
})

const COMMAND_WORDS = new Set(['stop', 'cancel', 'quiet', 'wait', 'pause', 'enough', 'shush'])
const words = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s']/gu, ' ')
    .split(/\s+/)
    .filter(Boolean)

/**
 * True when `heard` is the user talking over the device rather than the microphone picking up
 * the device's own speech: a command word, or two or more words mostly not in `speaking`.
 */
export function isBargeIn(heard: string, speaking: string): boolean {
  const h = words(heard)
  if (h.length === 1 && COMMAND_WORDS.has(h[0]!)) return true
  if (h.length < 2) return false
  const said = new Set(words(speaking))
  const foreign = h.filter((w) => !said.has(w)).length
  return foreign / h.length >= 0.5
}

type R = { state: VoiceState; effects: VoiceEffect[] }
const LISTEN: VoiceEffect = { type: 'listen', continuous: true }
const rest = (s: VoiceState): VoiceStatus => (s.handsFree ? 'listening' : 'idle')

export function voiceReducer(s: VoiceState, e: VoiceEvent): R {
  switch (e.type) {
    case 'PTT_DOWN': {
      const effects: VoiceEffect[] = []
      if (s.status === 'speaking') effects.push({ type: 'cancelSpeech' })
      if (s.status === 'thinking') effects.push({ type: 'cancelTurn' })
      // Hands-free already has the recognizer running; push-to-talk starts it.
      if (!s.handsFree) effects.push(LISTEN)
      return {
        state: {
          ...s,
          status: 'listening',
          pttHeld: true,
          buffer: '',
          interim: '',
          error: undefined,
        },
        effects,
      }
    }
    case 'PTT_UP': {
      if (!s.pttHeld) return { state: s, effects: [] }
      const state = { ...s, pttHeld: false }
      if (s.handsFree) {
        // In hands-free, releasing the button submits what was said while it was held.
        const text = s.buffer.trim()
        return text
          ? {
              state: { ...state, status: 'thinking', buffer: '', heard: text },
              effects: [{ type: 'handle', text }],
            }
          : { state, effects: [] }
      }
      return { state, effects: [{ type: 'stopListening' }] }
    }
    case 'TOGGLE_HANDS_FREE': {
      if (s.handsFree)
        return {
          state: {
            ...s,
            handsFree: false,
            status: s.status === 'listening' ? 'idle' : s.status,
            interim: '',
          },
          effects: [{ type: 'abortListening' }],
        }
      return {
        state: {
          ...s,
          handsFree: true,
          status: s.status === 'idle' ? 'listening' : s.status,
          error: undefined,
        },
        effects: s.status === 'listening' && s.pttHeld ? [] : [LISTEN],
      }
    }
    case 'INTERIM': {
      if (s.status === 'speaking' || s.status === 'thinking') {
        if (!s.handsFree || !isBargeIn(e.text, s.caption)) return { state: s, effects: [] }
        return {
          state: { ...s, status: 'listening', interim: e.text },
          effects: [s.status === 'speaking' ? { type: 'cancelSpeech' } : { type: 'cancelTurn' }],
        }
      }
      if (s.status !== 'listening') return { state: s, effects: [] }
      return { state: { ...s, interim: e.text }, effects: [] }
    }
    case 'FINAL': {
      const text = e.text.trim()
      if (!text) return { state: s, effects: [] }
      if (s.pttHeld)
        return {
          state: { ...s, buffer: s.buffer ? `${s.buffer} ${text}` : text, interim: '' },
          effects: [],
        }
      if (!s.handsFree) {
        // A late final after release (before RECOGNITION_END) still belongs to this turn.
        if (s.status !== 'listening') return { state: s, effects: [] }
        return {
          state: { ...s, buffer: s.buffer ? `${s.buffer} ${text}` : text, interim: '' },
          effects: [],
        }
      }
      if (s.status === 'speaking' || s.status === 'thinking') {
        if (!isBargeIn(text, s.caption)) return { state: s, effects: [] }
        const stop: VoiceEffect =
          s.status === 'speaking' ? { type: 'cancelSpeech' } : { type: 'cancelTurn' }
        return {
          state: { ...s, status: 'thinking', interim: '', heard: text },
          effects: [stop, { type: 'handle', text }],
        }
      }
      if (s.status !== 'listening') return { state: s, effects: [] }
      return {
        state: { ...s, status: 'thinking', interim: '', heard: text },
        effects: [{ type: 'handle', text }],
      }
    }
    case 'RECOGNITION_END': {
      if (s.pttHeld || s.handsFree) {
        // The browser stops after silence or a network blip; keep the microphone open.
        return { state: s, effects: [LISTEN] }
      }
      if (s.status !== 'listening') return { state: s, effects: [] }
      const text = s.buffer.trim()
      if (!text) return { state: { ...s, status: 'idle', interim: '', buffer: '' }, effects: [] }
      return {
        state: { ...s, status: 'thinking', interim: '', buffer: '', heard: text },
        effects: [{ type: 'handle', text }],
      }
    }
    case 'RECOGNITION_ERROR': {
      if (e.error === 'no-speech' || e.error === 'aborted') return { state: s, effects: [] }
      const denied = e.error === 'not-allowed' || e.error === 'service-not-allowed'
      const error = denied
        ? 'Microphone access was denied. Allow it in the browser to talk to Viko.'
        : `Listening stopped (${e.error}).`
      return {
        state: {
          ...s,
          handsFree: denied ? false : s.handsFree,
          pttHeld: false,
          status: s.status === 'listening' ? 'idle' : s.status,
          interim: '',
          error,
        },
        effects: [],
      }
    }
    case 'SPEAK': {
      if (!e.text.trim()) return { state: s, effects: [] }
      // Only a turn in progress speaks; a stale chunk after cancel or barge-in stays silent.
      if (s.status !== 'thinking' && s.status !== 'speaking') return { state: s, effects: [] }
      const caption = s.status === 'speaking' ? `${s.caption} ${e.text}`.trim() : e.text.trim()
      return {
        state: { ...s, status: 'speaking', caption },
        effects: [{ type: 'speak', text: e.text }],
      }
    }
    case 'SPEECH_DONE':
      if (s.status !== 'speaking') return { state: s, effects: [] }
      return { state: { ...s, status: rest(s) }, effects: [] }
    case 'TURN_DONE':
      if (s.status !== 'thinking') return { state: s, effects: [] }
      return { state: { ...s, status: rest(s) }, effects: [] }
    case 'TEXT': {
      const text = e.text.trim()
      if (!text) return { state: s, effects: [] }
      const effects: VoiceEffect[] = []
      if (s.status === 'speaking') effects.push({ type: 'cancelSpeech' })
      if (s.status === 'thinking') effects.push({ type: 'cancelTurn' })
      effects.push({ type: 'handle', text })
      return {
        state: { ...s, status: 'thinking', interim: '', buffer: '', heard: text },
        effects,
      }
    }
    case 'CANCEL':
      return {
        state: { ...s, status: rest(s), pttHeld: false, interim: '', buffer: '' },
        effects: [
          { type: 'cancelSpeech' },
          { type: 'cancelTurn' },
          ...(s.handsFree ? [] : [{ type: 'abortListening' } as const]),
        ],
      }
  }
}
